import React from 'react';

/** اجزای مشترک پنل منتور — تا سه تب ظاهر یکدست داشته باشند */

export const fa = (n: number) => n.toLocaleString('fa-IR');

export const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

export const HEALTH: Record<string, { label: string; color: string; hint: string }> = {
  GREEN: { label: 'سبز', color: '#0ca30c', hint: 'به‌روز است' },
  YELLOW: { label: 'زرد', color: '#fab219', hint: 'نیازمند پیگیری' },
  RED: { label: 'قرمز', color: '#d03b3b', hint: 'نیازمند توجه فوری' },
};

export const fullName = (u?: { firstName?: string | null; lastName?: string | null } | null) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim() || 'بدون نام';

/** حروف اول نام برای آواتار متنی — بدون نیاز به تصویر */
export const initials = (u?: { firstName?: string | null; lastName?: string | null } | null) => {
  const f = (u?.firstName || '').trim();
  const l = (u?.lastName || '').trim();
  if (f && l) return f[0] + l[0];
  return (f || l || '؟').slice(0, 2);
};

/**
 * فاصلهٔ زمانی به فارسی محاوره‌ای.
 * برای منتور «۳ روز پیش» بلافاصله معنا دارد، برخلاف یک تاریخ خام.
 */
export const timeAgo = (iso?: string | null) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days >= 30) return fa(Math.floor(days / 30)) + ' ماه پیش';
  if (days >= 1) return fa(days) + ' روز پیش';
  const hours = Math.floor(diff / 3600000);
  if (hours >= 1) return fa(hours) + ' ساعت پیش';
  return 'همین حالا';
};

export const daysSince = (iso?: string | null) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

export const Avatar: React.FC<{
  user?: { firstName?: string | null; lastName?: string | null } | null;
  tone?: string;
  size?: 'sm' | 'md';
}> = ({ user, tone = '#64748b', size = 'md' }) => (
  <span
    className={
      'flex shrink-0 items-center justify-center rounded-full font-black ' +
      (size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs')
    }
    style={{ backgroundColor: tone + '1a', color: tone }}
  >
    {initials(user)}
  </span>
);

export const HealthBadge: React.FC<{ health: string }> = ({ health }) => {
  const h = HEALTH[health] ?? HEALTH.GREEN;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-black"
      style={{ backgroundColor: h.color + '18', color: h.color }}
      title={h.hint}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: h.color }} />
      {h.label}
    </span>
  );
};

export const Progress: React.FC<{ value: number }> = ({ value }) => {
  const tone = value >= 70 ? '#0ca30c' : value >= 35 ? '#2563eb' : '#fab219';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: Math.max(2, value) + '%', backgroundColor: tone }}
        />
      </div>
      <span className="text-[11px] font-bold tabular-nums text-gray-600">{fa(value)}٪</span>
    </div>
  );
};

/** کارت آمار بالای صفحه؛ قابل کلیک برای فیلترکردن فهرست */
export const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: string;
  hint?: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, icon, tone, hint, active, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={
        'flex items-center gap-3 rounded-xl border bg-white p-3 text-right transition ' +
        (onClick ? 'hover:border-gray-300 ' : '') +
        (active ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200')
      }
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: tone + '15', color: tone }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-black leading-tight text-gray-900">{fa(value)}</span>
        <span className="block truncate text-[11px] font-bold text-gray-500">{label}</span>
        {hint ? <span className="block truncate text-[10px] text-gray-400">{hint}</span> : null}
      </span>
    </Tag>
  );
};

export const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  body?: string;
}> = ({ icon, title, body }) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
      {icon}
    </div>
    <p className="text-sm font-bold text-gray-700">{title}</p>
    {body ? <p className="mx-auto mt-1 max-w-sm text-xs leading-6 text-gray-500">{body}</p> : null}
  </div>
);
